/**
 * V73 — 402-triggered top-up picker.
 *
 * Lives in a single mounted-once spot (DashboardLayout) and listens for
 * the `plan-limit-exceeded` event that the Axios interceptor dispatches.
 * When fired with `topupPacks` in the detail, renders a pack picker;
 * when empty (structural limits like BRAND_COUNT), renders a plain
 * upgrade CTA.
 *
 * Click a pack → POST /api/billing/workspaces/:id/topups/checkout →
 * redirect to Mollie checkoutUrl. The billing page detects the
 * `?topup=success` return param and shows a processing banner while the
 * webhook catches up (~30–60s).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowRight, Loader2, Plus, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { topupApi, type PublicTopupPack } from '@/lib/api';
import { useWorkspaceContext } from '@/components/WorkspaceProvider';
import { ModalOverlay } from '@/components/posts/ModalPrimitives';
import { Button } from '@/components/ui/Button';

interface PlanLimitErrorDetail {
  limitType: string;
  current: number;
  limit: number;
  plan: string;
  error: string;
  upgradeUrl?: string;
  topupPacks: PublicTopupPack[];
}

export function TopupPickerModal() {
  const { t } = useTranslation();
  const { activeWorkspace } = useWorkspaceContext();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PlanLimitErrorDetail | null>(null);
  const [checkoutPending, setCheckoutPending] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Subscribe once at mount. api.ts dispatches `plan-limit-exceeded`
  // with the full PlanLimitError in event.detail.
  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<PlanLimitErrorDetail>;
      setDetail(customEvent.detail);
      setCheckoutError(null);
    };
    window.addEventListener('plan-limit-exceeded', handler);
    return () => window.removeEventListener('plan-limit-exceeded', handler);
  }, []);

  if (!detail) return null;

  // Beta / trial / paused plans can't purchase top-ups (backend 402s).
  // Hide any embedded packs so the modal collapses to the plan-limit-
  // only message — clicking a pack would just bounce off the backend
  // gate. Mirrors TopupSection's branch on the billing page.
  const canPurchase = detail.plan !== 'beta' && detail.plan !== 'trial' && detail.plan !== 'paused';
  const hasPacks = canPurchase && detail.topupPacks && detail.topupPacks.length > 0;

  const buyPack = async (packKey: string) => {
    if (!activeWorkspace?.id) return;
    setCheckoutPending(packKey);
    setCheckoutError(null);
    try {
      const response = await topupApi.checkout(activeWorkspace.id, packKey);
      // Stub mode (local dev without Mollie key) redirects to the
      // success page directly; real Mollie sends a /pay/ URL.
      window.location.href = response.checkoutUrl;
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setCheckoutError(e.response?.data?.message ?? e.message ?? 'Checkout failed');
      setCheckoutPending(null);
    }
  };

  const close = () => {
    setDetail(null);
    setCheckoutError(null);
    setCheckoutPending(null);
  };

  return (
    <ModalOverlay onClose={close}>
      <div
        data-testid="topup-picker-modal"
        className="bg-card relative w-full max-w-md rounded-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="border-border flex items-start justify-between gap-3 border-b px-6 pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="bg-accent/15 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg">
              <Sparkles className="text-accent h-5 w-5" />
            </div>
            <div>
              <h3 className="font-heading text-text-primary text-lg font-bold">
                {hasPacks ? t('topup.pickerTitle') : t('topup.planLimitTitle')}
              </h3>
              <p className="text-text-secondary mt-0.5 text-xs">
                {hasPacks
                  ? t('topup.pickerSubtitle', {
                      limitName: friendlyLimitName(detail.limitType, t),
                    })
                  : t('topup.planLimitSubtitle', {
                      limitName: friendlyLimitName(detail.limitType, t),
                      plan: detail.plan,
                    })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-text-tertiary hover:text-text-primary"
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {hasPacks ? (
            <>
              <ul className="space-y-2">
                {detail.topupPacks.map((pack) => {
                  const pending = checkoutPending === pack.packKey;
                  return (
                    <li key={pack.packKey}>
                      <button
                        type="button"
                        onClick={() => buyPack(pack.packKey)}
                        disabled={!!checkoutPending}
                        data-testid={`topup-pack-${pack.packKey}`}
                        className="border-border bg-subtle/40 hover:border-accent hover:bg-accent/5 flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all disabled:opacity-60"
                      >
                        <span className="flex items-center gap-3">
                          <Plus className="text-accent h-4 w-4" />
                          <span>
                            <span className="text-text-primary block text-sm font-semibold">
                              {pack.displayName}
                            </span>
                            <span className="text-text-tertiary block text-xs">
                              €{Number(pack.priceEur).toFixed(2).replace('.', ',')}
                            </span>
                          </span>
                        </span>
                        {pending ? (
                          <Loader2 className="text-accent h-4 w-4 animate-spin" />
                        ) : (
                          <ArrowRight className="text-text-tertiary h-4 w-4" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <p className="text-text-tertiary mt-4 text-[11px] leading-snug">
                {t('topup.processingHint')}
              </p>
              {checkoutError && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-400">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>{checkoutError}</span>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-text-secondary text-sm">
                {t('topup.planLimitBody', { limitName: friendlyLimitName(detail.limitType, t) })}
              </p>
              <Button
                onClick={() => {
                  close();
                  navigate(detail.upgradeUrl ?? '/dashboard/settings/billing');
                }}
                className="w-full justify-center"
              >
                {t('topup.upgradePlan')}
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-border border-t px-6 py-3 text-right">
          <button
            type="button"
            onClick={close}
            className="text-text-tertiary hover:text-text-primary text-xs"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

/**
 * Human-readable label for a LimitType enum value. Keeps the modal copy
 * aligned with what users see elsewhere instead of leaking the raw
 * MONTHLY_REELS_WITH_AUDIO enum name.
 */
function friendlyLimitName(limitType: string, t: (key: string) => string): string {
  switch (limitType) {
    case 'MONTHLY_POST_GENERATION':
      return t('topup.resourcePosts');
    case 'VISUAL_GENERATION':
      return t('topup.resourceImages');
    case 'MONTHLY_REEL_GENERATION':
      return t('topup.resourceReelsNoAudio');
    case 'MONTHLY_REELS_WITH_AUDIO':
      return t('topup.resourceReelsWithAudio');
    case 'BRAND_COUNT':
      return t('topup.resourceBrands');
    case 'PLATFORM_COUNT':
      return t('topup.resourcePlatforms');
    case 'TEAM_MEMBER_COUNT':
      return t('topup.resourceTeamMembers');
    case 'API_KEY_COUNT':
      return t('topup.resourceApiKeys');
    default:
      return limitType;
  }
}
